import { useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import { Theme, useTheme } from '@material-ui/core/styles';
import React from 'react';
import DocumentTitle from 'react-document-title';

import { internalClientIds, JobType } from 'shared/common/job-constants';
import { currencyFormat } from 'shared/common/string-format-utils';
import {
    awaitingClientStage,
    clientFinalRoundStage,
    clientFirstRoundStage,
    hiredStage,
    offerStage,
    rocketScreenCompleteStage
} from 'shared/models/job-stages';

import { createDuration, defaultPeriods, Duration } from '../common/duration';
import { PageDialogLink } from '../common/page-dialog-link';
import { getStatsMap, getTotalStat, Stat, UserRecruitingStats } from '../common/recruiter-stats';
import { SearchTextField } from '../common/search-text-field';
import { isoDate } from '../common/timestamp';
import { FUNNEL_STATS } from '../graphql/queries/recruiter-stats';
import { MetricsVariables } from '../graphql/queries/recruiter-stats';
import { FunnelStats } from '../graphql/queries/recruiter-stats';
import { useLocalStorage } from '../hooks/use-local-storage';
import { usePeriodFunnelCandidates } from '../hooks/use-period-funnel-candidates';
import { CellValue, DataPanel } from './data-panel';
import { DataPanelColumn, dataPanelContainerStyles, selectorStyles } from './data-panel';
import { DurationSelector } from './duration-selector';
import { FunnelStat } from './funnel-stat';
import { JobsTypeSelector, jobTypesForFilter } from './jobs-type-selector';
import { UserMetrics } from './metrics/user-metrics';
import { NavigationSelect } from './navigation-select';
import { RecruitingPageHeader, recruitingPageRoutes } from './recruiting-page-header';
import { TableColumnsSelector } from './table-columns-selector';

type Column =
    | 'recruiter'
    | 'outreachSent'
    | 'responseRate'
    | 'phoneScreens'
    | 'submits'
    | 'clientAccepts'
    | 'finalRounds'
    | 'offers'
    | 'hired'
    | 'recruiterBillings'
    | 'amBillings'
    | 'commission';

const columns: Array<DataPanelColumn<Column> & { label: string }> = [
    { id: 'recruiter', label: 'Recruiter' },
    { id: 'outreachSent', label: 'Outreach Sent' },
    { id: 'responseRate', label: 'Response Rate' },
    { id: 'phoneScreens', label: 'Phone Screens' },
    { id: 'submits', label: 'Submits' },
    { id: 'clientAccepts', label: 'Accepts' },
    { id: 'finalRounds', label: 'Final Rounds' },
    { id: 'offers', label: 'Offers' },
    { id: 'hired', label: 'Hires' },
    { id: 'recruiterBillings', label: 'Recruiter Billings' },
    { id: 'amBillings', label: 'AM Billings' },
    { id: 'commission', label: 'Commission' }
];

const columnLabels = columns.map((column) => column.label);
const defaultDisplayedColumns = [
    'Recruiter',
    'Outreach Sent',
    'Phone Screens',
    'Submits',
    'Accepts',
    'Offers',
    'Commission'
].reduce((selected, column) => ({ ...selected, [column]: true }), {});

const styles = (theme: Theme) => css`
    ${dataPanelContainerStyles(theme)};

    .selectors > div {
        display: flex;
        align-items: center;
    }

    .stat-main {
        font-weight: 500;
        font-size: 16px;
    }

    .funnel-stat {
        padding: 5px 0;
    }

    .recruiter-name {
        font-weight: 500;
        font-size: 14px;
        cursor: pointer;
        color: ${theme.palette.text.primary};

        &:hover {
            color: ${theme.palette.primary.main};
        }
    }
`;

export const RecruiterStats: React.FC<{}> = () => {
    const theme = useTheme();
    const [duration, setDuration] = React.useState<Duration>(createDuration({ period: 'quarter', offset: 0 }));
    const [jobTypes, setJobTypes] = useLocalStorage<JobType[]>(
        'recruiter-stats-job-types',
        jobTypesForFilter.get('all')
    );
    const [selectedColumns, setSelectedColumns] = useLocalStorage<{ [column: string]: boolean }>(
        'recruiter-stats-columns',
        defaultDisplayedColumns
    );
    const [searchText, setSearchText] = React.useState('');
    const allJobTypes = jobTypesForFilter.get('all');
    const excludeJobTypes = allJobTypes.filter((jt) => jobTypes.indexOf(jt) === -1);
    const { data: funnelData } = useQuery<FunnelStats, MetricsVariables>(FUNNEL_STATS, {
        variables: {
            endDate: isoDate(duration.end),
            excludeClientIds: internalClientIds,
            excludeJobTypes,
            startDate: isoDate(duration.start)
        }
    });
    const { data: funnelBaselineData } = useQuery<FunnelStats, MetricsVariables>(FUNNEL_STATS, {
        variables: {
            endDate: isoDate(duration.baselineEnd),
            excludeClientIds: internalClientIds,
            excludeJobTypes,
            startDate: isoDate(duration.baselineStart)
        }
    });
    const { setPeriodFunnelCandidatesData } = usePeriodFunnelCandidates();

    const handleStatClick = (label: string, record: UserRecruitingStats, stat: Stat, stage: string) => () => {
        if (stat?.value) {
            setPeriodFunnelCandidatesData({
                excludeClientIds: internalClientIds,
                excludeJobTypes,
                label,
                period: { startDate: isoDate(duration.start), endDate: isoDate(duration.end) },
                stage,
                user: record.user
            });
        }
    };

    const moneyFormat = (val: number) => `$${Math.round(val).toLocaleString()}`;
    const percentageFormat = (val: number) => `${val.toLocaleString()}%`;

    const handleSelectColumn = (column: string, selected: boolean) => {
        setSelectedColumns((previous) => ({ ...previous, [column]: selected }));
    };

    const statsMap = getStatsMap(funnelData, funnelBaselineData);
    const data =
        funnelData && funnelBaselineData
            ? Array.from(statsMap.dataMap.values()).filter((r) =>
                  [
                      r.outreachesSent,
                      r.outreachResponseRatio,
                      r.phoneScreens,
                      r.submits,
                      r.clientAccepts,
                      r.finalRounds,
                      r.offers,
                      r.hired,
                      r.recruiterBillings,
                      r.amBillings,
                      r.commission
                  ].some((stat) => stat.value || stat.periodBaseline)
              )
            : undefined;

    const sortableColumns = columns.map((c) => c.id);

    const rowFilter = React.useCallback(
        (stat: UserRecruitingStats) => {
            return stat.user.name.toLowerCase().includes(searchText.toLowerCase());
        },
        [searchText]
    );

    const getCellValue = (column: Column) => (record: UserRecruitingStats) => {
        switch (column) {
            case 'recruiter':
                return record.user.name;
            case 'outreachSent':
                return record.outreachesSent.value;
            case 'responseRate':
                return record.outreachResponseRatio.value;
            case 'phoneScreens':
                return record.phoneScreens.value;
            case 'submits':
                return record.submits.value;
            case 'clientAccepts':
                return record.clientAccepts.value;
            case 'finalRounds':
                return record.finalRounds.value;
            case 'offers':
                return record.offers.value;
            case 'hired':
                return record.hired.value;
            case 'recruiterBillings':
                return record.recruiterBillings.value;
            case 'amBillings':
                return record.amBillings.value;
            case 'commission':
                return record.commission.value;
            default:
                return null;
        }
    };

    const renderTotalStat = (stat: Stat, description: string, formatter?: (value: number) => string) => (
        <FunnelStat
            data={stat}
            description={description}
            period={duration.period}
            baselinePeriod={duration.baselinePeriod}
            formatter={formatter}
        />
    );

    const getTotalValue = (column: Column, filteredRows: readonly UserRecruitingStats[]) => {
        switch (column) {
            case 'recruiter':
                return <span className="recruiter-name">Total</span>;
            case 'outreachSent':
                return renderTotalStat(getTotalStat(filteredRows, 'outreachesSent'), 'outreach emails sent');
            case 'phoneScreens':
                return renderTotalStat(getTotalStat(filteredRows, 'phoneScreens'), 'phone screens');
            case 'submits':
                return renderTotalStat(getTotalStat(filteredRows, 'submits'), 'submits');
            case 'clientAccepts':
                return renderTotalStat(getTotalStat(filteredRows, 'clientAccepts'), 'client accepts');
            case 'finalRounds':
                return renderTotalStat(getTotalStat(filteredRows, 'finalRounds'), 'final rounds');
            case 'offers':
                return renderTotalStat(getTotalStat(filteredRows, 'offers'), 'offers');
            case 'hired':
                return renderTotalStat(getTotalStat(filteredRows, 'hired'), 'hires');
            case 'recruiterBillings':
                return renderTotalStat(
                    getTotalStat(filteredRows, 'recruiterBillings'),
                    'in recruiter billings',
                    currencyFormat
                );
            case 'amBillings':
                return renderTotalStat(getTotalStat(filteredRows, 'amBillings'), 'in AM billings', currencyFormat);
            case 'commission':
                return renderTotalStat(getTotalStat(filteredRows, 'commission'), 'in commissions', currencyFormat);
            default:
                return ' - ';
        }
    };

    const getCellRenderer = {
        amBillings: (_1: CellValue, record: UserRecruitingStats) => (
            <span className="stat-main">{moneyFormat(record.amBillings.value)}</span>
        ),
        clientAccepts: (_1: CellValue, record: UserRecruitingStats) => (
            <FunnelStat
                data={record.clientAccepts}
                description="client accepts"
                period={duration.period}
                baselinePeriod={duration.baselinePeriod}
                onClick={handleStatClick('Client Accepts', record, record.clientAccepts, clientFirstRoundStage)}
            />
        ),
        commission: (_1: CellValue, record: UserRecruitingStats) => (
            <span className="stat-main">{moneyFormat(record.commission.value)}</span>
        ),
        finalRounds: (_1: CellValue, record: UserRecruitingStats) => (
            <FunnelStat
                data={record.finalRounds}
                description="final rounds"
                period={duration.period}
                baselinePeriod={duration.baselinePeriod}
                onClick={handleStatClick('Final Rounds', record, record.finalRounds, clientFinalRoundStage)}
            />
        ),
        hired: (_1: CellValue, record: UserRecruitingStats) => (
            <FunnelStat
                data={record.hired}
                description="hires"
                period={duration.period}
                baselinePeriod={duration.baselinePeriod}
                onClick={handleStatClick('Hires', record, record.hired, hiredStage)}
            />
        ),
        offers: (_1: CellValue, record: UserRecruitingStats) => (
            <FunnelStat
                data={record.offers}
                description="offers"
                period={duration.period}
                baselinePeriod={duration.baselinePeriod}
                onClick={handleStatClick('Offers', record, record.offers, offerStage)}
            />
        ),
        outreachSent: (_1: CellValue, record: UserRecruitingStats) => (
            <FunnelStat
                data={record.outreachesSent}
                description="outreach emails sent"
                period={duration.period}
                baselinePeriod={duration.baselinePeriod}
            />
        ),
        phoneScreens: (_1: CellValue, record: UserRecruitingStats) => (
            <FunnelStat
                data={record.phoneScreens}
                description="phone screens"
                period={duration.period}
                baselinePeriod={duration.baselinePeriod}
                onClick={handleStatClick('Phone calls', record, record.phoneScreens, rocketScreenCompleteStage)}
            />
        ),
        recruiter: (_1: CellValue, record: UserRecruitingStats) => (
            <PageDialogLink
                url={`/user/${record.user.username}/metrics`}
                Component={UserMetrics}
                componentProps={{ username: record.user.username }}
            >
                <span className="recruiter-name">{record.user.name}</span>
            </PageDialogLink>
        ),
        recruiterBillings: (_1: CellValue, record: UserRecruitingStats) => (
            <span className="stat-main">{moneyFormat(record.recruiterBillings.value)}</span>
        ),
        responseRate: (_1: CellValue, record: UserRecruitingStats) => (
            <FunnelStat
                data={record.outreachResponseRatio}
                description="response rate"
                period={duration.period}
                baselinePeriod={duration.baselinePeriod}
                formatter={percentageFormat}
            />
        ),
        submits: (_1: CellValue, record: UserRecruitingStats) => (
            <FunnelStat
                data={record.submits}
                description="submits"
                period={duration.period}
                baselinePeriod={duration.baselinePeriod}
                onClick={handleStatClick('Submissions', record, record.submits, awaitingClientStage)}
            />
        )
    };

    const displayedColumns = columns.filter((column) => selectedColumns[column.label]);

    return (
        <DocumentTitle title="Recruiting Stats">
            <div id="container">
                <RecruitingPageHeader />
                <div css={styles(theme)}>
                    <div className="selectors">
                        <div css={selectorStyles}>
                            <SearchTextField value={searchText} onValueChange={setSearchText} variant="outlined" />
                            <NavigationSelect routes={recruitingPageRoutes} />
                            <DurationSelector selected={duration} onSelect={setDuration} periods={defaultPeriods} />
                            <JobsTypeSelector selected={jobTypes} onChange={setJobTypes} />
                            <TableColumnsSelector
                                columns={columnLabels}
                                selected={selectedColumns}
                                onSelect={handleSelectColumn}
                            />
                        </div>
                    </div>
                    <div className="data-panel">
                        <DataPanel
                            columns={displayedColumns}
                            data={data}
                            getCellValue={getCellValue}
                            getCellRenderer={getCellRenderer}
                            getTotalValue={getTotalValue}
                            sortableColumns={sortableColumns}
                            filterableColumns={['recruiter']}
                            initialSortColumn="commission"
                            initialSortDirection="desc"
                            rowFilter={rowFilter}
                            hidePagination={true}
                            pageSize={100}
                        />
                    </div>
                </div>
            </div>
        </DocumentTitle>
    );
};
